import { useState, useRef, useCallback, useEffect } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';

/**
 * Custom hook for observing all campaign SSE streams with reconnection support.
 * 
 * This hook connects to the unified observer endpoint which handles:
 * - Chat streaming completion signals
 * - Custom field generation status
 * - Email drafting status
 * - Campaign down-selection progress
 * - All EventBus events
 * 
 * On connection:
 * 1. Performs handshake - receives current state of all operations
 * 2. Streams all ongoing events
 * 3. Automatically reconnects on page reload
 * 
 * @param {string} campaignId - ID of the campaign to observe
 * @param {Object} callbacks - Event callbacks
 * @param {Function} callbacks.onHandshake - Called with handshake data (current state)
 * @param {Function} callbacks.onChatEnd - Called when chat streaming completes
 * @param {Function} callbacks.onStatus - Called with status updates
 * @param {Function} callbacks.onData - Called with data events (custom fields, emails, etc.)
 * @param {Function} callbacks.onError - Called on errors
 * 
 * @example
 * const { isConnected, reconnect } = useStreamObserver(campaignId, {
 *   onHandshake: (data) => {
 *     if (data.is_chat_streaming) {
 *       setShowThinking(true);
 *     }
 *     if (data.status) {
 *       setStatusUpdate(data.status);
 *     }
 *   },
 *   onChatEnd: () => {
 *     setShowThinking(false);
 *     loadChatHistory();
 *   },
 *   onStatus: (status) => setStatusUpdate(status),
 *   onData: (data) => handleDataUpdate(data)
 * });
 */
const useStreamObserver = (campaignId, { 
  onHandshake, 
  onChatEnd, 
  onStatus, 
  onData, 
  onError 
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const isUnmountedRef = useRef(false);
  const isConnectingRef = useRef(false);
  const retryAttemptRef = useRef(0);
  
  // Store callbacks in refs to avoid recreating connect function
  const callbacksRef = useRef({ onHandshake, onChatEnd, onStatus, onData, onError });
  
  // Update callbacks ref when they change
  useEffect(() => {
    callbacksRef.current = { onHandshake, onChatEnd, onStatus, onData, onError };
  }, [onHandshake, onChatEnd, onStatus, onData, onError]);

  const getBackoffMs = useCallback((attempt) => {
    const base = 1000; // 1s
    const cap = 15000; // 15s
    const jitter = Math.floor(Math.random() * 300);
    return Math.min(base * (2 ** Math.min(attempt, 4)), cap) + jitter;
  }, []);

  /**
   * Connect to the observer stream
   */
  const connect = useCallback(async () => {
    if (!campaignId || isUnmountedRef.current || isConnectingRef.current) return;

    // Prevent multiple simultaneous connection attempts
    isConnectingRef.current = true;

    // Abort existing connection if any
    if (eventSourceRef.current) {
      eventSourceRef.current.abort();
      eventSourceRef.current = null;
    }

    const token = localStorage.getItem('token');
    const anonymousUserId = localStorage.getItem('anonymous_user_id');
    
    if (!token && !anonymousUserId) {
      onError?.({ message: 'Authentication required' });
      return;
    }

    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const url = `${apiBase}/api/campaigns/${campaignId}/observe`;

    // Create abort controller for this connection
    const abortController = new AbortController();
    eventSourceRef.current = abortController;

    // Build headers
    const headers = {
      'Accept': 'text/event-stream',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else if (anonymousUserId) {
      headers['X-Anonymous-User-Id'] = anonymousUserId;
    }

    try {
      let sawObserverComplete = false;
      await fetchEventSource(url, {
        method: 'GET',
        headers,
        signal: abortController.signal,

        async onopen(response) {
          if (isUnmountedRef.current) return;

          if (response.ok) {
            isConnectingRef.current = false;
            setIsConnected(true);
            retryAttemptRef.current = 0;
            return;
          }

          // Handle error responses
          isConnectingRef.current = false;
          if (response.status === 401) {
            throw new Error('Authentication failed');
          } else if (response.status >= 400) {
            const text = await response.text();
            throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
          }
        },

        onmessage(event) {
          if (isUnmountedRef.current) return;

          // Skip keepalive comments
          if (!event.data) return;

          try {
            const data = JSON.parse(event.data);

            switch (data.event) {
              case 'handshake':
                callbacksRef.current.onHandshake?.(data.data);
                break;

              case 'observer_complete':
                sawObserverComplete = true;
                break;

              case 'chat_end':
                console.log('Chat streaming completed');
                callbacksRef.current.onChatEnd?.();
                break;

              case 'status':
                callbacksRef.current.onStatus?.(data.data);
                break;

              case 'heartbeat':
                // Ignore heartbeats (connection keepalive)
                break;

              case 'error':
                console.error('Observer error event:', data.data);
                callbacksRef.current.onError?.(data.data);
                break;

              case 'companies':
              case 'contacts':
              case 'custom_field_definition':
              case 'companies_custom_field_update':
              case 'contacts_custom_field_update':
              case 'sec_filings_custom_field_update':
                // Pass all data events to the data handler
                callbacksRef.current.onData?.(data);
                break;

              default:
                // Pass unknown events to data handler
                callbacksRef.current.onData?.(data);
                break;
            }
          } catch (e) {
            console.error('Error parsing observer event:', e);
          }
        },

        onerror(error) {
          if (isUnmountedRef.current) return;

          console.error('Observer connection error:', error);
          isConnectingRef.current = false;
          setIsConnected(false);

          // Throw to stop fetchEventSource's internal retry loop.
          // The catch block below handles surfacing errors and scheduling
          // reconnects, so we don't call onError here to avoid double-firing.
          throw error;
        },

        onclose() {
          if (isUnmountedRef.current) return;

          isConnectingRef.current = false;
          setIsConnected(false);

          // If the server closed after observer_complete, this is expected.
          if (sawObserverComplete) {
            return;
          }

          // Unexpected close while operations may still be in progress:
          // trigger reconnect flow through catch.
          throw new Error('Observer stream closed unexpectedly');
        },

        openWhenHidden: true,
      });

    } catch (error) {
      if (isUnmountedRef.current) return;

      isConnectingRef.current = false;
      setIsConnected(false);

      if (error.name === 'AbortError') return;

      retryAttemptRef.current += 1;

      // Network drops (TypeError) are transient — schedule a silent reconnect.
      // Only surface a hard error for auth failures or after repeated attempts.
      const isNetworkDrop = error instanceof TypeError;
      const MAX_SILENT_RETRIES = 5;
      if (!isNetworkDrop || retryAttemptRef.current > MAX_SILENT_RETRIES) {
        console.error('Observer connection failed (attempt %d):', retryAttemptRef.current, error);
        callbacksRef.current.onError?.(error);
      } else {
        console.warn('Observer connection dropped, reconnecting (attempt %d)...', retryAttemptRef.current);
      }

      if (!isUnmountedRef.current && !reconnectTimeoutRef.current) {
        const delayMs = getBackoffMs(retryAttemptRef.current);
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectTimeoutRef.current = null;
          connect();
        }, delayMs);
      }
    }
  }, [campaignId, getBackoffMs]);

  /**
   * Manually trigger reconnection
   */
  const reconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    retryAttemptRef.current = 0;
    connect();
  }, [connect]);

  /**
   * Disconnect from the observer stream
   */
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.abort();
      eventSourceRef.current = null;
    }

    isConnectingRef.current = false;
    retryAttemptRef.current = 0;
    setIsConnected(false);
  }, []);

  // Connect when campaign ID changes
  useEffect(() => {
    if (campaignId) {
      connect();
    }

    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isUnmountedRef.current = true;
      disconnect();
    };
  }, [disconnect]);

  return {
    isConnected,
    reconnect,
    disconnect
  };
};

export default useStreamObserver;

